import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Building2,
  Globe,
  Phone,
  Mail,
  MapPin,
  Users,
  Save,
  Plus,
  Trash2,
  ExternalLink,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

// Animated counter component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const target = parseInt(value) || 0;
    const increment = target / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString('fr-FR');
  };

  return <span ref={countRef}>{formatNumber(count)}</span>;
};

// Social media icons
const SocialIcons = {
  facebook: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  instagram: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  linkedin: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  twitter: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  youtube: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  tiktok: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  )
};

const socialColors = {
  facebook: '#1877F2',
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  twitter: '#000000',
  youtube: '#FF0000',
  tiktok: '#000000'
};

const CompanyProfilePage = () => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [company, setCompany] = useState({
    name: user?.company_name || '',
    legal_name: '',
    siret: '',
    vat_number: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    description: '',
    industry: '',
    employees_count: '',
    founded_year: ''
  });

  const [socialMedia, setSocialMedia] = useState([
    { platform: 'facebook', url: 'https://facebook.com/testcorp', followers: 12500 },
    { platform: 'instagram', url: 'https://instagram.com/testcorp', followers: 8340 },
    { platform: 'linkedin', url: 'https://linkedin.com/company/testcorp', followers: 3200 },
    { platform: 'twitter', url: 'https://x.com/testcorp', followers: 5600 },
    { platform: 'youtube', url: 'https://youtube.com/@testcorp', followers: 1200 },
  ]);

  const [teamMembers, setTeamMembers] = useState([
    { id: 1, name: 'Marie Client', email: 'marie@testcorp.com', role: 'admin', avatar: '' },
    { id: 2, name: 'Pierre Dupont', email: 'pierre@testcorp.com', role: 'manager', avatar: '' },
  ]);

  const totalFollowers = socialMedia.reduce((sum, s) => sum + (s.followers || 0), 0);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // API call would go here
      toast.success('Profil entreprise mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const addSocialMedia = () => {
    setSocialMedia([...socialMedia, { platform: 'facebook', url: '', followers: 0 }]);
  };

  const removeSocialMedia = (index) => {
    setSocialMedia(socialMedia.filter((_, i) => i !== index));
  };

  const updateSocialMedia = (index, field, value) => {
    const updated = [...socialMedia];
    updated[index][field] = value;
    setSocialMedia(updated);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header with Social Stats */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Profil entreprise
              </h1>
              <p className="text-[#64748b]">
                Gérez les informations de votre entreprise et vos réseaux sociaux
              </p>
            </div>
          </div>

          {/* Social Media Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {/* Total Followers */}
            <Card className="card col-span-2 md:col-span-1" style={{ background: `linear-gradient(135deg, ${theme.colors?.primary}, ${theme.colors?.accent})` }}>
              <CardContent className="p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm opacity-80">Total abonnés</span>
                </div>
                <p className="text-3xl font-bold">
                  <AnimatedCounter value={totalFollowers} />
                </p>
                <div className="flex items-center gap-1 mt-1 text-xs opacity-80">
                  <TrendingUp className="w-3 h-3" />
                  <span>+12% ce mois</span>
                </div>
              </CardContent>
            </Card>

            {/* Individual Social Stats */}
            {socialMedia.slice(0, 5).map((social) => {
              const Icon = SocialIcons[social.platform];
              return (
                <Card key={social.platform} className="card card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: socialColors[social.platform] }}
                      >
                        {Icon && <Icon />}
                      </div>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                      <AnimatedCounter value={social.followers} />
                    </p>
                    <p className="text-xs text-[#64748b] capitalize">{social.platform}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="info" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="info" className="gap-2">
              <Building2 className="w-4 h-4" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="social" className="gap-2">
              <Globe className="w-4 h-4" />
              Réseaux sociaux
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-2">
              <Users className="w-4 h-4" />
              Équipe
            </TabsTrigger>
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="info">
            <Card className="card">
              <CardHeader>
                <CardTitle>Informations de l'entreprise</CardTitle>
                <CardDescription>Détails légaux et coordonnées</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSave} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom commercial</Label>
                      <Input
                        value={company.name}
                        onChange={(e) => setCompany({ ...company, name: e.target.value })}
                        className="input-default"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Raison sociale</Label>
                      <Input
                        value={company.legal_name}
                        onChange={(e) => setCompany({ ...company, legal_name: e.target.value })}
                        className="input-default"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SIRET</Label>
                      <Input
                        value={company.siret}
                        onChange={(e) => setCompany({ ...company, siret: e.target.value })}
                        className="input-default"
                        placeholder="123 456 789 00012"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>N° TVA Intracommunautaire</Label>
                      <Input
                        value={company.vat_number}
                        onChange={(e) => setCompany({ ...company, vat_number: e.target.value })}
                        className="input-default"
                        placeholder="FR12345678901"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Site web</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                        <Input
                          value={company.website}
                          onChange={(e) => setCompany({ ...company, website: e.target.value })}
                          className="input-default pl-10"
                          placeholder="https://"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                        <Input
                          type="email"
                          value={company.email}
                          onChange={(e) => setCompany({ ...company, email: e.target.value })}
                          className="input-default pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                        <Input
                          value={company.phone}
                          onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                          className="input-default pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#64748b]" />
                      <Textarea
                        value={company.address}
                        onChange={(e) => setCompany({ ...company, address: e.target.value })}
                        className="input-default pl-10 min-h-[80px]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Code postal</Label>
                      <Input
                        value={company.postal_code}
                        onChange={(e) => setCompany({ ...company, postal_code: e.target.value })}
                        className="input-default"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input
                        value={company.city}
                        onChange={(e) => setCompany({ ...company, city: e.target.value })}
                        className="input-default"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pays</Label>
                      <Input
                        value={company.country}
                        onChange={(e) => setCompany({ ...company, country: e.target.value })}
                        className="input-default"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description de l'entreprise</Label>
                    <Textarea
                      value={company.description}
                      onChange={(e) => setCompany({ ...company, description: e.target.value })}
                      className="input-default min-h-[120px]"
                      placeholder="Décrivez votre activité..."
                    />
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      type="submit"
                      disabled={saving}
                      className="rounded-full"
                      style={{ backgroundColor: theme.colors?.accent }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Media Tab */}
          <TabsContent value="social">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Réseaux sociaux</CardTitle>
                  <CardDescription>Connectez vos comptes pour suivre vos statistiques</CardDescription>
                </div>
                <Button onClick={addSocialMedia} variant="outline" className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialMedia.map((social, index) => {
                  const Icon = SocialIcons[social.platform];
                  return (
                    <div key={index} className="flex items-center gap-4 p-4 bg-[#f8fafc] rounded-xl">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: socialColors[social.platform] }}
                      >
                        {Icon && <Icon />}
                      </div>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <select
                          value={social.platform}
                          onChange={(e) => updateSocialMedia(index, 'platform', e.target.value)}
                          className="px-3 py-2 border rounded-xl"
                        >
                          <option value="facebook">Facebook</option>
                          <option value="instagram">Instagram</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="twitter">X (Twitter)</option>
                          <option value="youtube">YouTube</option>
                          <option value="tiktok">TikTok</option>
                        </select>
                        <Input
                          value={social.url}
                          onChange={(e) => updateSocialMedia(index, 'url', e.target.value)}
                          placeholder="URL du profil"
                          className="input-default"
                        />
                        <Input
                          type="number"
                          value={social.followers}
                          onChange={(e) => updateSocialMedia(index, 'followers', parseInt(e.target.value) || 0)}
                          placeholder="Nombre d'abonnés"
                          className="input-default"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        {social.url && (
                          <Button variant="ghost" size="icon" className="rounded-full" asChild>
                            <a href={social.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full text-red-500"
                          onClick={() => removeSocialMedia(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-full"
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Équipe</CardTitle>
                  <CardDescription>Gérez les utilisateurs de votre entreprise</CardDescription>
                </div>
                <Button variant="outline" className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Inviter
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {teamMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-xl">
                      <div className="flex items-center gap-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                          style={{ backgroundColor: theme.colors?.primary }}
                        >
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: theme.colors?.primary }}>
                            {member.name}
                          </p>
                          <p className="text-sm text-[#64748b]">{member.email}</p>
                        </div>
                      </div>
                      <Badge className={cn(
                        member.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      )}>
                        {member.role === 'admin' ? 'Administrateur' : 'Gestionnaire'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CompanyProfilePage;
